// stores/subscription.ts
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAuthUserStore } from './auth';
import AutoService from '@services/AutoService';

interface Plan {
  PK: string;
  SK: string;
  planId: string;
  name: string;
  type: 'subscription' | 'credits'
  priceEUR: number;
  credits: number;
  description: string;
  billingPeriod: 'monthly' | 'yearly' | 'one-time';
  features: {
    estimatedUsage: {
      storageGB: string;
      transcriptionMinutes: string;
      translationMinutes: string;
      humanCorrectionMinutes: string;
    };
    /** Free storage allowance in GB; absent = backend default (STORAGE_FREE_GB). */
    storageFreeGB?: number;
  };
}

interface Credits {
  available: number;
  total: number;
  usageThisMonth: number;
}


interface Subscription {
  PK: string;
  SK: string;
  id: string;
  name: string;
  status: 'active' | 'canceled' | 'past_due' | 'pending' | 'canceling' | 'incomplete' | 'incomplete_expired';
  planId: string;
  current_period_start: string;
  current_period_end: string;
  canceledAt?: string;
  cancel_at_period_end: boolean;
  billingCycle: 'monthly' | 'yearly';
  autoRenew: boolean;
}

interface EnhancedSubscription extends Subscription {
  daysUntilExpiration: number;
  isExpiringSoon: boolean;
  plan?: Plan;
}

export interface Invoice {
  PK: string;
  SK: string;
  type: string;
  invoiceId: string;
  amount: number;
  currency: string;
  metadata: string;
  status: 'paid' | 'pending' | 'failed';
  description: string;
  credits?: number;
  subscriptionId?: string;
  planId?: string;
  priceWithVAT: number;
  priceWithoutVAT: number;
  vatAmount: number;
  ronAmountWithVAT: number;
  ronAmountWithoutVAT: number;
  ronVATAmount: number;
  pdfUrl?: string;
  createdAt: string;
  stripeInvoiceId: string;
  billingPeriod?: string;
}

export const useSubscriptionStore = defineStore('subscription', () => {
  // State
  const isLoading = ref(false);
  const plans = ref<Plan[]>([]);
  const currentSubscriptions = ref<Subscription[] | null>(null);
  const invoices = ref<Invoice[]>([]);
  const lastRefresh = ref<Date | null>(null);
  const EXPIRING_SOON_THRESHOLD = 7; // Days threshold for "expiring soon" warning

  // In useSubscriptionStore
  const credits = ref<Credits>({
    available: 0,
    total: 0,
    usageThisMonth: 0
  });

 

  const authStore = useAuthUserStore();

   // Helper function to calculate days until expiration
   function calculateDaysUntilExpiration(endDate: string): number {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }


    // Enhanced computed property for subscriptions with additional data
    const enhancedSubscriptions = computed((): EnhancedSubscription[] => {
      if (!currentSubscriptions.value) return [];
      //console.log('Computing enhanced subscriptions');
      return currentSubscriptions.value.map(subscription => {
        const daysLeft = calculateDaysUntilExpiration(subscription.current_period_end);
        const plan = plans.value.find(p => p.planId === subscription.planId);
        
        return {
          ...subscription,
          daysUntilExpiration: daysLeft,
          isExpiringSoon: daysLeft <= EXPIRING_SOON_THRESHOLD && daysLeft > 0,
          plan: plan
        };
      });
    });

const groupedPlans = computed(() => {
  const groups: { [key: string]: {
    key: PropertyKey | undefined;
    monthly: any;
    yearly: any;
    planId: string;
    name: string;
    plans: Plan[] 
  } } = {};

    if (plans.value)
      plans.value.forEach(plan => {
        const [planId] = plan.planId.split('_');
        if (plan.billingPeriod == undefined) plan.billingPeriod = 'one-time';
        if (!groups[planId]) {
          groups[planId] = {
            key: planId,
            monthly: null,
            yearly: null,
            planId,
            name: planId.trim(),
            plans: []
          };
        }
        groups[planId].plans.push(plan);
      });
    return Object.values(groups);
  });


    // Computed property for subscription status
    const subscriptionStatus = computed(() => {
      if (!enhancedSubscriptions.value.length) return 'free';
      if (enhancedSubscriptions.value.some(sub => sub.status === 'past_due')) return 'past_due';
      if (enhancedSubscriptions.value.some(sub => sub.status === 'active')) return 'active';
      return 'inactive';
    });
    
  // Actions
    async function fetchCredits() {
      try {
        // GET subscription/credits is a direct DynamoDB GetItem on the ledger table
        // (Account#<sub> / Account#<sub>#tokens) → { Item: { balance, denomination, ... } }.
        // The store state is the Credits shape — map the response into it instead of
        // overwriting the ref with the raw item(s) (which made credits.available
        // undefined and permanently disabled every credits gate, e.g. TranscribePanel).
        const response: any = await AutoService.fetchOrder('subscription/credits');
        const item = Array.isArray(response?.Items)
          ? response.Items[0]
          : response?.Item ?? response;
        credits.value = {
          available: Number(item?.available ?? item?.balance ?? 0),
          total: Number(item?.total ?? item?.balance ?? 0),
          usageThisMonth: Number(item?.usageThisMonth ?? 0),
        };
      } catch (error) {
        console.error('Error fetching credits:', error);
      }
    }
    
  async function fetchPlans() {
    try {
      const response = await AutoService.fetchOrder('subscription/plans');
      plans.value = (Array.isArray(response.Items) ? response.Items : [response]) as Plan[];
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  }

  async function updateSubscription(Item: Subscription) {
    try {
      if (Item) {
        const updatedSubscription = Item as Subscription;
        const index = currentSubscriptions.value?.findIndex(sub => sub.SK === updatedSubscription.SK);
        if (index !== undefined && index !== -1) {
          currentSubscriptions.value![index] = updatedSubscription;
        }
      }

    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  async function fetchSubsciptions() {
    try {
     
      const response = await AutoService.fetchOrder('subscription/current');
      
      if (response && Array.isArray(response.Items) && response.Items.length > 0) {
        currentSubscriptions.value = response.Items;
      } else {
        currentSubscriptions.value = null;
      }
      
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      currentSubscriptions.value = null;
      throw error;
    }
  }

  async function fetchInvoices() {
    try {
      
      const response = await AutoService.fetchOrder('subscription/invoices');
      if (response && Array.isArray(response.Items)) {
        invoices.value = response.Items as Invoice[];
      }
      else 
      {
        invoices.value = [];
      }
      
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  }

  // Helper function to get plan by ID
  function getPlanById(planId: string): Plan | undefined {
    return plans.value.find(plan => plan.planId === planId);
  }

  // Refresh all subscription data
  async function refresh() {
    if (isLoading.value) return;
    
    isLoading.value = true;
    try {
      await Promise.all([
        fetchPlans(),
        fetchSubsciptions(),
        fetchCredits(),
        fetchInvoices()
      ]);
      lastRefresh.value = new Date();
    } catch (error) {
      console.error('Error refreshing subscription data:', error);
    } finally {
      isLoading.value = false;
    }
  }

  // Initialize store
  function init() {
    if (authStore.isAuthenticated) {
      refresh();
    }
  }

  // Helper computed for large credit packages
  const largeCreditPackages = computed(() => {
    return plans.value.filter(plan => 
      plan.type === 'credits' && 
      plan.credits >= 10000 // 10,000+ credits (166+ hours) considered "large"
    ).sort((a, b) => b.credits - a.credits); // Sort by credits descending
  });

  // Helper computed for subscription plans
  const subscriptionPlans = computed(() => {
    return plans.value.filter(plan => plan.type === 'subscription')
      .sort((a, b) => a.priceEUR - b.priceEUR); // Sort by price ascending
  });

  // Helper computed for regular credit packages
  const regularCreditPackages = computed(() => {
    return plans.value.filter(plan => 
      plan.type === 'credits' && 
      plan.credits < 10000 // Less than 10,000 credits
    ).sort((a, b) => a.priceEUR - b.priceEUR); // Sort by price ascending
  });

  // Helper to format credits display
  const formatCreditsDisplay = (credits: number): string => {
    if (credits >= 60000) {
      return `${(credits / 60).toLocaleString()} hours`; // Show as hours for large packages
    } else if (credits >= 60) {
      return `${Math.floor(credits / 60)} hours ${credits % 60} min`;
    } else {
      return `${credits} credits`;
    }
  };

  return {
    // State
    isLoading,
    plans,
    currentSubscriptions,
    invoices,
    lastRefresh,
    credits,
    // Getters
    groupedPlans,
    enhancedSubscriptions,
    subscriptionStatus,
    updateSubscription,
    // Actions
    fetchPlans,
    fetchSubsciptions,
    fetchInvoices,
    refresh,
    getPlanById,
    fetchCredits,
    init,
    // New computed properties
    largeCreditPackages,
    subscriptionPlans,
    regularCreditPackages,
    formatCreditsDisplay
  } 
});